import { useCallback } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import {
  attestAllSupportingDocuments,
  attestSupportingDocument,
  rejectSupportingDocument,
  revokeDocumentApproval,
  revokeDocumentRejection,
  type AttestAllSupportingDocumentsParams,
  type AttestAllSupportingDocumentsResult,
  type AttestSupportingDocumentParams,
  type AttestSupportingDocumentResult,
  type RejectSupportingDocumentParams,
  type RejectSupportingDocumentResult,
  type RevokeDocumentApprovalParams,
  type RevokeDocumentRejectionParams,
} from "src-next/modules/patient-supporting-documents";

export function useDocumentAttestationActions() {
  const attestTask = useCallback(
    (params: AttestSupportingDocumentParams) =>
      attestSupportingDocument(params),
    [],
  );

  const attestAllTask = useCallback(
    (params: AttestAllSupportingDocumentsParams) =>
      attestAllSupportingDocuments(params),
    [],
  );

  const rejectTask = useCallback(
    (params: RejectSupportingDocumentParams) =>
      rejectSupportingDocument(params),
    [],
  );

  const revokeApprovalTask = useCallback(
    (params: RevokeDocumentApprovalParams) => revokeDocumentApproval(params),
    [],
  );

  const revokeRejectionTask = useCallback(
    (params: RevokeDocumentRejectionParams) => revokeDocumentRejection(params),
    [],
  );

  const { run: runAttest, loading: attesting } = useAsyncTask<
    [AttestSupportingDocumentParams],
    AttestSupportingDocumentResult
  >({
    task: attestTask,
  });

  const { run: runAttestAll, loading: attestingAll } = useAsyncTask<
    [AttestAllSupportingDocumentsParams],
    AttestAllSupportingDocumentsResult
  >({
    task: attestAllTask,
  });

  const { run: runReject, loading: rejecting } = useAsyncTask<
    [RejectSupportingDocumentParams],
    RejectSupportingDocumentResult
  >({
    task: rejectTask,
  });

  const { run: runRevokeApproval, loading: revokingApproval } = useAsyncTask<
    [RevokeDocumentApprovalParams],
    { documentReferenceId: string }
  >({
    task: revokeApprovalTask,
  });

  const { run: runRevokeRejection, loading: revokingRejection } = useAsyncTask<
    [RevokeDocumentRejectionParams],
    { documentReferenceId: string }
  >({
    task: revokeRejectionTask,
  });

  return {
    runAttest,
    runAttestAll,
    runReject,
    runRevokeApproval,
    runRevokeRejection,
    attesting,
    attestingAll,
    rejecting,
    revokingApproval,
    revokingRejection,
  };
}
